import { FC, useMemo } from 'react';
import {
    CurrentPlanProjectionDetailsData,
    ErrorMessage,
    MaturityValuesProjectedEnumType,
    PensionStatementDataEntryMethod,
    ProjectedValuesValues,
    ReferenceData,
} from '@xplan-pension-switching/common';
import {
    IressCol,
    IressForm,
    IressFormField,
    IressFormFieldset,
    IressInput,
    IressRadio,
    IressRadioGroup,
    IressRow,
    IressStack,
} from '@iress-oss/ids-components';
import { Section } from '../../Section/Section';
import { ProjectedMaturityBasisFields } from './ProjectedMaturityBasisFields';
import { AssumedChargesAndGrowthRatesFields } from './AssumedChargesAndGrowthRatesFields';

export type ProjectedValuesProps = {
    projectedValues: ProjectedValuesValues;
    clientAge: number;
    referenceData: ReferenceData;
};

export const ProjectedValues: FC<ProjectedValuesProps> = props => {
    const { projectedValues, clientAge, referenceData } = props;

    const { watch } = IressForm.useFormContext<CurrentPlanProjectionDetailsData>();
    const maturityValueProjectedDefault = useMemo(
        () => projectedValues?.maturityValuesProjected ?? MaturityValuesProjectedEnumType.Age,
        [projectedValues]
    );
    const maturityValuesProjectedValue = watch(
        'projectedValues.maturityValuesProjected',
        maturityValueProjectedDefault
    );
    const pensionStatementDataEntryMethodDefault = useMemo(
        () =>
            projectedValues?.pensionStatementDataEntryMethod ?? PensionStatementDataEntryMethod.ProjectedMaturityBasis,
        [projectedValues]
    );
    const pensionStatementDataEntryMethod = watch(
        'projectedValues.pensionStatementDataEntryMethod',
        pensionStatementDataEntryMethodDefault
    );

    return (
        <Section heading="Projected values" headingLevel="h3">
            <IressStack gutter="lg">
                <IressFormFieldset
                    label="Projection to"
                    name="projectedValues.maturityValuesProjected"
                    defaultValue={maturityValueProjectedDefault}
                    rules={{ required: ErrorMessage.SelectAnOption }}
                    render={controlledProps => (
                        <IressRadioGroup layout="inline" {...controlledProps}>
                            <IressRadio data-testid="rdRetirementAge" value={MaturityValuesProjectedEnumType.Age}>
                                Retirement age
                            </IressRadio>
                            <IressRadio data-testid="rdSpecificTerm" value={MaturityValuesProjectedEnumType.Term}>
                                Specific term
                            </IressRadio>
                        </IressRadioGroup>
                    )}
                />
                <IressRow gutter={'lg'}>
                    <IressCol span="3">
                        {maturityValuesProjectedValue === MaturityValuesProjectedEnumType.Term && (
                            <IressFormField
                                label="Term"
                                name="projectedValues.term"
                                defaultValue={projectedValues?.term}
                                rules={{
                                    required: ErrorMessage.EnterAValue,
                                    min: {
                                        value: 1,
                                        message: `Enter a number between 1 and ${referenceData.maximumTermForCalculations}`,
                                    },
                                    max: {
                                        value: referenceData.maximumTermForCalculations,
                                        message: `Enter a number between 1 and ${referenceData.maximumTermForCalculations}`,
                                    },
                                }}
                                render={controlledProps => (
                                    <IressInput {...controlledProps} type="number" data-testid="txtTerm" />
                                )}
                            />
                        )}
                        {maturityValuesProjectedValue === MaturityValuesProjectedEnumType.Age && (
                            <IressFormField
                                label="Retirement age"
                                name="projectedValues.retirementAge"
                                defaultValue={projectedValues?.retirementAge}
                                rules={{
                                    required: ErrorMessage.EnterAValue,
                                    min: {
                                        value: Math.max(referenceData.minimumRetirementAge, clientAge + 1),
                                        message:
                                            clientAge < referenceData.minimumRetirementAge
                                                ? `Enter a valid retirement age (minimum age is ${referenceData.minimumRetirementAge})`
                                                : `Enter a valid retirement age (minimum age is ${clientAge + 1})`,
                                    },
                                    max: {
                                        value: referenceData.maximumRetirementAge,
                                        message: `Enter a value less than or equal to ${referenceData.maximumRetirementAge}`,
                                    },
                                }}
                                render={controlledProps => (
                                    <IressInput {...controlledProps} type="number" data-testid="txtRetirementAge" />
                                )}
                            />
                        )}
                    </IressCol>
                </IressRow>
                <IressFormFieldset
                    label="Select a pension statement data entry method"
                    name="projectedValues.pensionStatementDataEntryMethod"
                    defaultValue={
                        projectedValues?.pensionStatementDataEntryMethod ??
                        PensionStatementDataEntryMethod.ProjectedMaturityBasis
                    }
                    rules={{ required: ErrorMessage.SelectAnOption }}
                    render={controlledProps => (
                        <IressRadioGroup layout="inline" {...controlledProps}>
                            <IressRadio
                                data-testid="rdProjectedMaturityBasis"
                                value={PensionStatementDataEntryMethod.ProjectedMaturityBasis}
                            >
                                Projected maturity basis
                            </IressRadio>
                            <IressRadio
                                data-testid="rdAssumedChargesAndGrowthRates"
                                value={PensionStatementDataEntryMethod.AssumedChargesAndGrowthRates}
                            >
                                Assumed charges and growth rates
                            </IressRadio>
                        </IressRadioGroup>
                    )}
                />
                {pensionStatementDataEntryMethod === PensionStatementDataEntryMethod.ProjectedMaturityBasis && (
                    <ProjectedMaturityBasisFields projectedValues={projectedValues} />
                )}
                {pensionStatementDataEntryMethod === PensionStatementDataEntryMethod.AssumedChargesAndGrowthRates && (
                    <AssumedChargesAndGrowthRatesFields projectedValues={projectedValues} />
                )}
                <IressFormField
                    name="projectedValues.clientAge"
                    hiddenLabel
                    hidden
                    style={{ display: 'none' }}
                    defaultValue={clientAge}
                    render={controlledProps => (
                        <IressInput {...controlledProps} type="hidden" data-testid="clientAge" />
                    )}
                    label="Client age"
                />
            </IressStack>
        </Section>
    );
};
